import { sample } from 'effector';
import _, { get } from 'lodash';
import $$auth from 'models/auth';
import $$ep from 'models/ep';
import { $qInstances } from 'models/q-instances';
import { $global } from 'models/store-global';

import EstatePlanService from 'services/EstatePlanService';
import { filterTrueValues } from 'utils/common-helper';

import { type EstatePlanProductChoice, EstatePlanProductChoiceZod } from 'types/zod/product-choice';

// EstatePlanService.getQuestionnaire mirroring to $epQuestionnaire
sample({
    source: {
        stateCode: $$auth.$currentParticipant.map(
            (currentParticipant) => currentParticipant?.domicile_state_code || '',
        ),
        questionnaires: $global.map((state) => state.questionnaires),
    },
    fn: ({ stateCode, questionnaires }) => questionnaires?.[stateCode]?.EP || null,
    target: $$ep.stores.$epQuestionnaire,
});

// EstatePlanService.getQInstance mirroring to $epQInstance
sample({
    source: $qInstances.map(
        (qInstances) =>
            Object.values(qInstances).find((qInstance) => qInstance.questionnaire_type_code === 'EP') || null,
    ),
    target: $$ep.stores.$epQInstance,
});

sample({
    source: $$ep.stores.$epQInstance,
    fn: (qInstance) => qInstance?.questionnaire_instance_id ?? null,
    target: $$ep.stores.$epQInstanceId,
});

// EstatePlanService.getQInstance mirroring to $epQInstance
sample({
    source: {
        answers: $global.map((state) => state.answers),
        qInstanceId: $$ep.stores.$epQInstanceId,
    },
    fn: ({ answers, qInstanceId }) => answers?.[qInstanceId || -1] || null,
    target: $$ep.stores.$epAnswers,
});

sample({
    source: $$ep.stores.$epAnswers.map((answers) => !get(answers, EstatePlanService.withSpousePath)),
    target: $$ep.$isSingle,
});

sample({
    source: $$ep.stores.$epAnswers
        .map((answers) => get(answers, EstatePlanService.chooseProductPath, {}))
        .map((answerByPath) => (Object.keys(answerByPath)?.[0] as EstatePlanProductChoice | undefined) || null),
    target: $$ep.$selectedProduct,
});

sample({
    source: $global
        .map((state) => state.temp?.plans || [])
        .map(
            (plans) =>
                _(plans)
                    .map('product_choice')
                    .uniq()
                    .value()
                    .filter(
                        (productChoice) => EstatePlanProductChoiceZod.safeParse(productChoice).success,
                    ) as EstatePlanProductChoice[],
        ),
    target: $$ep.$productChoices,
});

sample({
    source: {
        selectedProduct: $$ep.$selectedProduct,
        productChoices: $$ep.$productChoices,
    },
    fn: ({ selectedProduct, productChoices }) =>
        (productChoices || []).findIndex((product) => product === selectedProduct) || 0,
    target: $$ep.$selectedProductIndex,
});

sample({
    source: $$ep.stores.$epAnswers,
    fn: (answers) => {
        const paths = EstatePlanService.getAssessmentPaths();
        const congratulatedAnswers = filterTrueValues(EstatePlanService.getCongratulationAnswers(answers));
        const assessmentAnswers = filterTrueValues(_.pick(answers || {}, paths));
        const isFirstTime = !get(answers || {}, 'initial_questions.congratulation');
        return !_.isEqual(congratulatedAnswers, assessmentAnswers) || isFirstTime;
    },
    target: $$ep.$isCongratulationAnswersUpdated,
});

sample({
    source: $$ep.stores.$epAnswers,
    fn: EstatePlanService.getCongratulationPaths,
    target: $$ep.$congradulationPaths,
});
